import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../context/AuthContext';

function DepositManagement() {
  const { currentUser } = useAuth();
  
  // Check if current user is admin or merchant
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';

  // State for deposits data
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    userId: '',
    merchantId: '',
    transactionType: '',
    startDate: '',
    endDate: ''
  });
  const [merchants, setMerchants] = useState([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  
  // State for deposit creation/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'delete'
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    amount: '',
    transactionType: 'deposit',
    notes: '',
    referenceId: ''
  });
  
  // State for users dropdown
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // State for notification snackbar
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch deposits when component loads and when page/rowsPerPage changes
  useEffect(() => {
    fetchDeposits();
    fetchUsers();
    if (isAdmin) {
      fetchMerchants();
    }
  }, [page, rowsPerPage, filters]);

  // Function to fetch merchants for filter dropdown
  const fetchMerchants = async () => {
    setLoadingMerchants(true);
    try {
      const response = await axios.get('/admin/merchants');
      setMerchants(response.data.merchants || []);
      setLoadingMerchants(false);
    } catch (err) {
      setLoadingMerchants(false);
      console.error('Error fetching merchants:', err);
    }
  };

  // Function to fetch deposits with pagination
  const fetchDeposits = async () => {
    setLoading(true);
    try {
      // Build query parameters
      let queryParams = `limit=${rowsPerPage}&offset=${page * rowsPerPage}`;
      
      // Add filters if they are set
      if (filters.userId) {
        queryParams += `&user_id=${filters.userId}`;
      }
      if (filters.merchantId) {
        queryParams += `&merchant_id=${filters.merchantId}`;
      }
      if (filters.transactionType) {
        queryParams += `&transaction_type=${filters.transactionType}`;
      }
      if (filters.startDate) {
        queryParams += `&start_date=${filters.startDate}`;
      }
      if (filters.endDate) {
        queryParams += `&end_date=${filters.endDate}`;
      }
      
      const response = await axios.get(`/deposit/all?${queryParams}`);
      setDeposits(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setLoading(false);
    } catch (err) {
      setError('Failed to load deposits');
      setLoading(false);
      showNotification('Failed to load deposits', 'error');
      console.error('Deposits fetch error:', err);
    }
  };

  // Function to fetch all users for the dropdown
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      // Get current user info from auth context to check role
      const currentUserResponse = await axios.get('/auth/current-user');
      const currentUser = currentUserResponse.data.user;
      
      let appUsersResponse;
      
      // Filter users by merchant_id if the current user is a merchant
      if (currentUser && currentUser.role === 'merchant' && currentUser.merchant_id) {
        // Fetch only app users belonging to this merchant
        appUsersResponse = await axios.get(`/admin/app-users?merchant_id=${currentUser.merchant_id}`);
      } else {
        // For admin, fetch all app users
        appUsersResponse = await axios.get('/admin/app-users');
      }
      
      setUsers(appUsersResponse.data.users || appUsersResponse.data.appUsers || []);
      setLoadingUsers(false);
    } catch (err) {
      setLoadingUsers(false);
      console.error('App users fetch error:', err);
      setError('Could not load app users for selection.');
    }
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Reset to first page when filter changes
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      userId: '',
      merchantId: '',
      transactionType: '',
      startDate: '',
      endDate: ''
    });
    setPage(0);
  };

  // Open dialog for creating a new deposit
  const handleCreateDialogOpen = () => {
    setDialogMode('create');
    setSelectedDeposit(null);
    setFormData({
      userId: '',
      amount: '',
      transactionType: 'deposit',
      notes: '',
      referenceId: ''
    });
    setDialogOpen(true);
  };

  // Open dialog for editing a deposit
  const handleEditDialogOpen = (deposit) => {
    setDialogMode('edit');
    setSelectedDeposit(deposit);
    setFormData({
      userId: deposit.user_id.toString(),
      amount: deposit.amount.toString(),
      transactionType: deposit.transaction_type || 'deposit',
      notes: deposit.notes || '',
      referenceId: deposit.reference_id || ''
    });
    setDialogOpen(true);
  };

  // Open dialog for deleting a deposit
  const handleDeleteDialogOpen = (deposit) => {
    setDialogMode('delete');
    setSelectedDeposit(deposit);
    setDialogOpen(true);
  };

  // Close dialog
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle deposit create/edit/delete submission
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        // Create new deposit
        await axios.post('/deposit/add', {
          userId: formData.userId,
          amount: parseFloat(formData.amount),
          transactionType: formData.transactionType,
          notes: formData.notes,
          referenceId: formData.referenceId
        });
        
        showNotification('Deposit created successfully', 'success');
      } else if (dialogMode === 'edit') {
        // Edit existing deposit
        await axios.put(`/deposit/${selectedDeposit.id}`, {
          userId: formData.userId,
          amount: parseFloat(formData.amount),
          transactionType: formData.transactionType,
          notes: formData.notes,
          referenceId: formData.referenceId
        });
        
        showNotification('Deposit updated successfully', 'success');
      } else if (dialogMode === 'delete') {
        // Delete deposit
        await axios.delete(`/deposit/${selectedDeposit.id}`);
        
        showNotification('Deposit deleted successfully', 'success');
      }
      
      // Close dialog and refresh deposits
      handleDialogClose();
      fetchDeposits();
    } catch (err) {
      console.error('Error processing deposit action:', err);
      showNotification(
        err.response?.data?.message || 'Failed to process request',
        'error'
      );
    }
  };

  // Function to show notifications
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Get color for transaction type chip
  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'deposit':
        return 'success';
      case 'withdrawal':
        return 'warning';
      case 'call_charge':
        return 'primary';
      case 'credit':
        return 'error';
      case 'refund':
        return 'info';
      default:
        return 'default';
    }
  };

  // Format transaction amount for display
  const formatTransactionAmount = (amount, type) => {
    const formattedAmount = parseFloat(amount).toFixed(0);
    if (type === 'deposit' || type === 'refund') {
      return `+¥${formattedAmount}`;
    } else {
      return `-¥${formattedAmount}`;
    }
  };

  if (loading && deposits.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transaction History</Typography>
        <Box>
          <Button 
            startIcon={<RefreshIcon />} 
            variant="outlined" 
            onClick={fetchDeposits}
          >
            Refresh
          </Button>
          {/* Add New Deposit button hidden */}
          {/* <Button 
            startIcon={<AddIcon />} 
            variant="contained" 
            onClick={handleCreateDialogOpen}
            sx={{ ml: 2 }}
          >
            Add New Deposit
          </Button> */}
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                name="userId"
                value={filters.userId}
                onChange={handleFilterChange}
                label="User"
              >
                <MenuItem value="">All Users</MenuItem>
                {loadingUsers ? (
                  <MenuItem value="">
                    <Box display="flex" alignItems="center">
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                    </Box>
                  </MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username || user.email} (ID: {user.id})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Only show merchant filter for admin users */}
          {isAdmin && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Merchant</InputLabel>
                <Select
                  name="merchantId"
                  value={filters.merchantId}
                  onChange={handleFilterChange}
                  label="Merchant"
                >
                  <MenuItem value="">All Merchants</MenuItem>
                  {loadingMerchants ? (
                    <MenuItem value="">
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : (
                    merchants.map((merchant) => (
                      <MenuItem key={merchant.merchant_id} value={merchant.merchant_id}>
                        {merchant.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12} sm={6} md={isAdmin ? 1.5 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                name="transactionType"
                value={filters.transactionType}
                onChange={handleFilterChange}
                label="Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="deposit">Deposit</MenuItem>
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="call_charge">Call Charge</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="refund">Refund</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={isAdmin ? 2 : 2.5}>
            <TextField
              fullWidth
              label="Start Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={isAdmin ? 2 : 2.5}>
            <TextField
              fullWidth
              label="End Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={isAdmin ? 2.5 : 3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!filters.userId && !filters.merchantId && !filters.transactionType && !filters.startDate && !filters.endDate}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
        
        {/* Show active filters summary */}
        {(filters.userId || filters.merchantId || filters.transactionType || filters.startDate || filters.endDate) && (
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
              Active Filters:
            </Typography>
            {filters.userId && (
              <Chip 
                label={`User: ${users.find(u => u.id === parseInt(filters.userId))?.username || users.find(u => u.id === parseInt(filters.userId))?.email || filters.userId}`}
                size="small" 
                onDelete={() => handleFilterChange({ target: { name: 'userId', value: '' } })}
              />
            )}
            {filters.merchantId && (
              <Chip 
                label={`Merchant: ${merchants.find(m => m.merchant_id === parseInt(filters.merchantId))?.name || filters.merchantId}`}
                size="small"
                onDelete={() => handleFilterChange({ target: { name: 'merchantId', value: '' } })}
              />
            )}
            {filters.transactionType && (
              <Chip 
                label={`Type: ${filters.transactionType.replace('_', ' ')}`}
                size="small"
                onDelete={() => handleFilterChange({ target: { name: 'transactionType', value: '' } })}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
            {filters.startDate && (
              <Chip 
                label={`From: ${filters.startDate}`}
                size="small"
                onDelete={() => handleFilterChange({ target: { name: 'startDate', value: '' } })}
              />
            )}
            {filters.endDate && (
              <Chip 
                label={`To: ${filters.endDate}`}
                size="small"
                onDelete={() => handleFilterChange({ target: { name: 'endDate', value: '' } })}
              />
            )}
          </Box>
        )}
      </Paper>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table aria-label="deposits table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>
              ) : deposits.length > 0 ? (
                deposits.map((deposit) => (
                  <TableRow key={deposit.id}>
                    <TableCell>{deposit.id}</TableCell>
                    <TableCell>{deposit.username || `User ID: ${deposit.user_id}`}</TableCell>
                    <TableCell>{formatDate(deposit.created_at)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={deposit.transaction_type.replace('_', ' ')} 
                        color={getTransactionTypeColor(deposit.transaction_type)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {formatTransactionAmount(deposit.amount, deposit.transaction_type)}
                    </TableCell>
                    <TableCell>¥{parseFloat(deposit.balance).toFixed(0)}</TableCell>
                    <TableCell>{deposit.reference_id || '-'}</TableCell>
                    <TableCell>{deposit.notes}</TableCell>
                    <TableCell>
                      {/* Only show edit and delete buttons for admin users */}
                      {isAdmin && (
                        <>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleEditDialogOpen(deposit)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteDialogOpen(deposit)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {/* For merchant users, show read-only indicator */}
                      {isMerchant && (
                        <Typography variant="body2" color="textSecondary">
                          View Only
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">No deposits found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create/Edit Deposit Dialog */}
      <Dialog open={dialogOpen && dialogMode !== 'delete'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Deposit' : 'Edit Deposit'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="user-select-label">User</InputLabel>
                <Select
                  labelId="user-select-label"
                  id="user-select"
                  name="userId"
                  value={formData.userId}
                  onChange={handleInputChange}
                  label="User"
                  required
                >
                  {loadingUsers ? (
                    <MenuItem value="">
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={20} sx={{ mr: 1 }} /> Loading users...
                      </Box>
                    </MenuItem>
                  ) : (
                    users.map(user => (
                      <MenuItem key={user.id} value={user.id.toString()}>
                        {user.username || user.email} ({user.id})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
                <Select
                  labelId="transaction-type-label"
                  id="transaction-type"
                  name="transactionType"
                  value={formData.transactionType}
                  onChange={handleInputChange}
                  label="Transaction Type"
                  required
                >
                  <MenuItem value="deposit">Deposit</MenuItem>
                  <MenuItem value="withdrawal">Withdrawal</MenuItem>
                  <MenuItem value="call_charge">Call Charge</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                  <MenuItem value="refund">Refund</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference ID"
                name="referenceId"
                value={formData.referenceId}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            color="primary"
            disabled={!formData.userId || !formData.amount}
          >
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialogOpen && dialogMode === 'delete'} onClose={handleDialogClose}>
        <DialogTitle>Delete Deposit</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete this deposit record? This action cannot be undone.
          </Alert>
          {selectedDeposit && (
            <Box mt={2}>
              <Typography>
                <strong>Date:</strong> {formatDate(selectedDeposit.created_at)}
              </Typography>
              <Typography>
                <strong>User ID:</strong> {selectedDeposit.user_id}
              </Typography>
              <Typography>
                <strong>Amount:</strong> {formatTransactionAmount(selectedDeposit.amount, selectedDeposit.transaction_type)}
              </Typography>
              <Typography>
                <strong>Type:</strong> {selectedDeposit.transaction_type}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DepositManagement;